import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Component, ViewChild, Output, Input, NgModule } from '@angular/core';
import * as i2 from '@pv-frontend/pv-shared-components/svg-icon';
import { SvgIconModule } from '@pv-frontend/pv-shared-components/svg-icon';
import * as i4 from '@pv-frontend/pv-shared-directives/tracking';
import { ClickTrackingModule } from '@pv-frontend/pv-shared-directives/tracking';
import * as i3 from 'primeng/progressspinner';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Subject, debounceTime } from 'rxjs';

class SearchBarComponent {
    constructor() {
        this.subject = new Subject();
        this.DEFAULT_LOADER_FILL = 'var(--primary-color)';
        this.DEFAULT_ICON_SIZE = {
            width: '0.8rem',
            height: '1.2rem',
        };
        this.DEFAULT_LOADER_STROKE_WIDTH = '3';
        this.DEFAULT_SEARCH_PLACEHOLDER = 'Search...';
        this.DEFAULT_DEBOUNCE_DUE_TIME = 500;
        this.appliedStyles = this.style;
        this.searchQueryChange = new EventEmitter();
        this.backTo = new EventEmitter();
        this.showBackIcon = false; //To show backBtnIcon in place of search icon
        this.debounceDueTime = this.DEFAULT_DEBOUNCE_DUE_TIME;
        this.isLoading = false;
        this.placeholder = this.DEFAULT_SEARCH_PLACEHOLDER;
        this.autoFocus = false;
        this.analyticsEnabled = false;
        this.labelForAnalytics = '';
    }
    ngOnInit() {
        this.appliedStyles = {
            ...this.style,
            backIcon: { ...this.DEFAULT_ICON_SIZE, ...this.style?.backIcon },
            searchIcon: { ...this.DEFAULT_ICON_SIZE, ...this.style?.searchIcon },
            loader: {
                icon: { ...this.DEFAULT_ICON_SIZE, ...this.style?.loader?.icon },
                strokeWidth: this.style?.loader?.strokeWidth,
                fill: this.style?.loader?.fill,
            },
        };
        this.subject.pipe(debounceTime(this.debounceDueTime)).subscribe({
            next: (input) => {
                this.searchQueryChange.emit(input);
            },
        });
    }
    ngAfterViewInit() {
        if (this.autoFocus) {
            this.searchInputRef?.nativeElement?.focus();
        }
    }
    handleBackClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.backTo.emit();
    }
    handleInputChange(inputEvent) {
        const searchInput = inputEvent.target?.value ?? '';
        this.subject.next(searchInput);
    }
}
SearchBarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: SearchBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SearchBarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: SearchBarComponent, selector: "pv-search-bar", inputs: { showBackIcon: "showBackIcon", debounceDueTime: "debounceDueTime", isLoading: "isLoading", placeholder: "placeholder", autoFocus: "autoFocus", style: "style", analyticsEnabled: "analyticsEnabled", labelForAnalytics: "labelForAnalytics" }, outputs: { searchQueryChange: "searchQueryChange", backTo: "backTo" }, viewQueries: [{ propertyName: "searchInputRef", first: true, predicate: ["searchInputRef"], descendants: true }], ngImport: i0, template: "<span\n  class=\"pv-search-bar-container border-round-3xl flex px-3 py-2 align-items-center justify-content-center w-full surface-border border-1\"\n  [style]=\"appliedStyles?.container\">\n  <pv-svg-icon\n    *ngIf=\"showBackIcon; else searchIcon\"\n    name=\"back\"\n    styleClass=\"cursor-pointer\"\n    [style]=\"appliedStyles?.backIcon\"\n    (click)=\"handleBackClick($event)\"></pv-svg-icon>\n  <ng-template #searchIcon>\n    <pv-svg-icon\n      name=\"search\"\n      [style]=\"appliedStyles?.searchIcon\"></pv-svg-icon>\n  </ng-template>\n  <input\n    class=\"pv-search-input outline-none shadow-none border-none w-full px-2 py-1 bg-transparent\"\n    [style]=\"appliedStyles?.input\"\n    [placeholder]=\"placeholder ?? DEFAULT_SEARCH_PLACEHOLDER\"\n    (input)=\"handleInputChange($event)\"\n    [pvClickTrack]=\"this.analyticsEnabled\"\n    [pvTrackLabel]=\"this.labelForAnalytics\"\n    #searchInputRef />\n  <p-progressSpinner\n    class=\"flex\"\n    *ngIf=\"isLoading\"\n    [style]=\"appliedStyles?.loader?.icon\"\n    [strokeWidth]=\"\n      appliedStyles?.loader?.strokeWidth || DEFAULT_LOADER_STROKE_WIDTH\n    \"\n    [fill]=\"\n      appliedStyles?.loader?.fill || DEFAULT_LOADER_FILL\n    \"></p-progressSpinner>\n</span>\n", styles: [".pv-search-input{caret-color:var(--primary-color)}.pv-search-bar-container:has(.pv-search-input:focus){border-color:var(--primary-color)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.SvgIconComponent, selector: "pv-svg-icon", inputs: ["name", "srcUrl", "style", "styleClass"] }, { kind: "component", type: i3.ProgressSpinner, selector: "p-progressSpinner", inputs: ["style", "styleClass", "strokeWidth", "fill", "animationDuration"] }, { kind: "directive", type: i4.ClickTrackDirective, selector: "[pvClickTrack]", inputs: ["pvClickTrack", "pvTrackLabel"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: SearchBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'pv-search-bar', template: "<span\n  class=\"pv-search-bar-container border-round-3xl flex px-3 py-2 align-items-center justify-content-center w-full surface-border border-1\"\n  [style]=\"appliedStyles?.container\">\n  <pv-svg-icon\n    *ngIf=\"showBackIcon; else searchIcon\"\n    name=\"back\"\n    styleClass=\"cursor-pointer\"\n    [style]=\"appliedStyles?.backIcon\"\n    (click)=\"handleBackClick($event)\"></pv-svg-icon>\n  <ng-template #searchIcon>\n    <pv-svg-icon\n      name=\"search\"\n      [style]=\"appliedStyles?.searchIcon\"></pv-svg-icon>\n  </ng-template>\n  <input\n    class=\"pv-search-input outline-none shadow-none border-none w-full px-2 py-1 bg-transparent\"\n    [style]=\"appliedStyles?.input\"\n    [placeholder]=\"placeholder ?? DEFAULT_SEARCH_PLACEHOLDER\"\n    (input)=\"handleInputChange($event)\"\n    [pvClickTrack]=\"this.analyticsEnabled\"\n    [pvTrackLabel]=\"this.labelForAnalytics\"\n    #searchInputRef />\n  <p-progressSpinner\n    class=\"flex\"\n    *ngIf=\"isLoading\"\n    [style]=\"appliedStyles?.loader?.icon\"\n    [strokeWidth]=\"\n      appliedStyles?.loader?.strokeWidth || DEFAULT_LOADER_STROKE_WIDTH\n    \"\n    [fill]=\"\n      appliedStyles?.loader?.fill || DEFAULT_LOADER_FILL\n    \"></p-progressSpinner>\n</span>\n", styles: [".pv-search-input{caret-color:var(--primary-color)}.pv-search-bar-container:has(.pv-search-input:focus){border-color:var(--primary-color)}\n"] }]
        }], propDecorators: { searchInputRef: [{
                type: ViewChild,
                args: ['searchInputRef']
            }], searchQueryChange: [{
                type: Output
            }], backTo: [{
                type: Output
            }], showBackIcon: [{
                type: Input
            }], debounceDueTime: [{
                type: Input
            }], isLoading: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], autoFocus: [{
                type: Input
            }], style: [{
                type: Input
            }], analyticsEnabled: [{
                type: Input
            }], labelForAnalytics: [{
                type: Input
            }] } });

class PvSearchBarModule {
}
PvSearchBarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PvSearchBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PvSearchBarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: PvSearchBarModule, declarations: [SearchBarComponent], imports: [CommonModule,
        SvgIconModule,
        ProgressSpinnerModule,
        ClickTrackingModule], exports: [SearchBarComponent] });
PvSearchBarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PvSearchBarModule, imports: [CommonModule,
        SvgIconModule,
        ProgressSpinnerModule,
        ClickTrackingModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PvSearchBarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SearchBarComponent],
                    imports: [
                        CommonModule,
                        SvgIconModule,
                        ProgressSpinnerModule,
                        ClickTrackingModule,
                    ],
                    exports: [SearchBarComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PvSearchBarModule, SearchBarComponent };
